import React from 'react';
import { Bookmark, Instagram, Youtube, Music, Zap, Share2 } from 'lucide-react';
import type { ContentIdea } from '../../../../stores/ideaStore';
import { useSavedIdeasStore } from '../../../../stores/savedIdeasStore';
import { TRANSITIONS } from '../../../../utils/animations';
import toast from 'react-hot-toast';

interface IdeaCardProps {
  idea: ContentIdea;
}

export default function IdeaCard({ idea }: IdeaCardProps) {
  const { savedIdeas, saveIdea, removeIdea } = useSavedIdeasStore();
  const isSaved = savedIdeas.some(saved => saved.title === idea.title);

  const handleSave = () => {
    if (isSaved) {
      removeIdea(idea);
      toast.success('Ideia removida dos salvos!');
    } else {
      saveIdea(idea);
      toast.success('Ideia salva com sucesso!');
    }
  };

  const getPlatformIcon = (platform: string) => {
    switch (platform) {
      case 'instagram':
        return <Instagram className="h-5 w-5 text-pink-600" />;
      case 'youtube':
        return <Youtube className="h-5 w-5 text-red-600" />;
      case 'tiktok':
        return <Music className="h-5 w-5 text-black dark:text-white" />;
      default:
        return <Zap className="h-5 w-5 text-primary-600" />;
    }
  };

  const getPlatformColor = (platform: string) => {
    switch (platform) {
      case 'instagram':
        return 'from-pink-50 to-purple-50 border-pink-200';
      case 'youtube':
        return 'from-red-50 to-orange-50 border-red-200';
      case 'tiktok':
        return 'from-cyan-50 to-blue-50 border-cyan-200';
      default:
        return 'from-indigo-50 to-blue-50 border-indigo-200';
    }
  };

  const handleShare = () => {
    // Create a text representation of the idea
    const shareText = `${idea.title}\n\n${idea.description}\n\n${idea.hashtags?.map(tag => `#${tag}`).join(' ')}`;
    
    // Try to use the Web Share API if available
    if (navigator.share) {
      navigator.share({
        title: idea.title,
        text: shareText
      }).catch(err => {
        console.error('Error sharing:', err);
        // Fallback to clipboard
        copyToClipboard(shareText);
      });
    } else {
      // Fallback to clipboard
      copyToClipboard(shareText);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      toast.success('Ideia copiada para a área de transferência!');
    }).catch(err => {
      console.error('Failed to copy:', err);
      toast.error('Erro ao copiar para a área de transferência');
    });
  };

  return (
    <div className={`p-5 bg-gradient-to-br ${getPlatformColor(idea.category)} rounded-lg border shadow-sm hover:shadow-md ${TRANSITIONS.CARD}`}>
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center space-x-2 mb-2">
            <div className={`${TRANSITIONS.ICON} group p-1.5 rounded-full bg-white`}>
              {getPlatformIcon(idea.category)}
            </div>
            <h3 className="font-medium text-gray-900">{idea.title}</h3>
          </div>
          <p className="text-gray-600 text-sm">{idea.description}</p>
          
          {idea.hashtags && idea.hashtags.length > 0 && (
            <div className="mt-3 bg-white/80 p-3 rounded-lg">
              <div className="flex flex-wrap gap-2">
                {idea.hashtags.map((tag, index) => (
                  <span
                    key={index}
                    className="text-xs text-indigo-600 font-mono bg-indigo-50 px-2 py-1 rounded-full"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
        
        <div className="flex flex-col space-y-2">
          <button
            onClick={handleSave}
            className={`p-2 ${TRANSITIONS.DEFAULT} rounded-full hover:bg-white/80 ${
              isSaved ? 'text-yellow-500 hover:text-yellow-600' : 'text-gray-400 hover:text-yellow-500'
            }`}
            title={isSaved ? 'Remover dos salvos' : 'Salvar ideia'}
          >
            <Bookmark className={`h-5 w-5 ${TRANSITIONS.DEFAULT}`} fill={isSaved ? 'currentColor' : 'none'} />
          </button>
          
          <button
            onClick={handleShare}
            className="p-2 text-gray-400 hover:text-indigo-600 rounded-full hover:bg-white/80 transition-colors"
            title="Compartilhar ideia"
          >
            <Share2 className="h-5 w-5" />
          </button>
        </div>
      </div>
      
      <div className="mt-3 pt-2 border-t border-gray-200/50 flex justify-between items-center">
        <span className="text-xs text-gray-500 capitalize">{idea.type}</span>
        <span className="text-xs text-gray-500 capitalize">{idea.category}</span>
      </div>
    </div>
  );
}